import React from 'react';
import { Reorder, useDragControls } from 'motion/react';
import { GripVertical, Trash2 } from 'lucide-react';
import { AddRowButton, AutoTextarea, IconButton } from '../editor/fields';
import { ProcessStep, generateId } from './types';

// Numbered vertical list of process steps ("How we did it").

interface Props {
  steps: ProcessStep[];
  onChange: (steps: ProcessStep[]) => void;
}

export const ProcessStepsSection: React.FC<Props> = ({ steps, onChange }) => {
  const update = (id: string, patch: Partial<ProcessStep>) => onChange(steps.map((s) => (s.id === id ? { ...s, ...patch } : s)));
  const remove = (id: string) => onChange(steps.filter((s) => s.id !== id));
  const add = () => onChange([...steps, { id: generateId(), title: '', description: '', sortOrder: steps.length }]);

  return (
    <div>
      {steps.length ? (
        <Reorder.Group as="div" axis="y" values={steps} onReorder={onChange} className="space-y-3">
          {steps.map((step, i) => (
            <StepRow key={step.id} step={step} index={i} onChange={(patch) => update(step.id, patch)} onDelete={() => remove(step.id)} />
          ))}
        </Reorder.Group>
      ) : (
        <p className="text-[12.5px] text-[color:var(--oa-muted)]">No steps yet.</p>
      )}
      <div className="mt-3">
        <AddRowButton onClick={add}>Add step</AddRowButton>
      </div>
    </div>
  );
};

const StepRow: React.FC<{ step: ProcessStep; index: number; onChange: (patch: Partial<ProcessStep>) => void; onDelete: () => void }> = ({ step, index, onChange, onDelete }) => {
  const controls = useDragControls();
  return (
    <Reorder.Item as="div" value={step} dragListener={false} dragControls={controls} className="group/row flex items-start gap-3 rounded-xl border border-[color:var(--oa-line-2)] bg-[color:var(--oa-card)] p-3">
      <button
        type="button"
        onPointerDown={(e) => controls.start(e)}
        className="mt-1.5 grid h-6 w-5 cursor-grab touch-none place-items-center rounded text-[color:var(--oa-faint)] hover:bg-[color:var(--oa-raised-2)] hover:text-[color:var(--oa-muted)] active:cursor-grabbing"
        aria-label="Drag to reorder"
      >
        <GripVertical className="h-4 w-4" />
      </button>
      <span className="mt-0.5 grid h-7 w-7 shrink-0 place-items-center rounded-full border-2 border-[color:var(--oa-accent)] font-montserrat text-[12px] font-bold text-[color:var(--oa-accent)]">{index + 1}</span>
      <div className="min-w-0 flex-1 space-y-1.5">
        <input
          value={step.title}
          onChange={(e) => onChange({ title: e.target.value })}
          placeholder="Step title"
          className="w-full bg-transparent text-[14px] font-semibold text-[color:var(--oa-text)] outline-none placeholder:text-[color:var(--oa-faint)]"
        />
        <AutoTextarea bare minRows={2} value={step.description} onChange={(e) => onChange({ description: e.target.value })} placeholder="Describe this step" className="text-[13px] leading-6 text-[color:var(--oa-muted)]" />
      </div>
      <IconButton tone="danger" onClick={onDelete} aria-label="Remove step" className="opacity-0 transition-opacity group-hover/row:opacity-100">
        <Trash2 className="h-3.5 w-3.5" />
      </IconButton>
    </Reorder.Item>
  );
};
